
import { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { parseUltraDetailedAnalysis, formatDate } from './enhanced-analysis/utils';
import { generateUltraDetailedPDF } from '@/utils/pdfGenerator';
import { EnhancedAdvancedAnalysisModalProps, GroupedSections } from './enhanced-analysis/types';
import ModalHeader from './enhanced-analysis/ModalHeader';
import ModalFooter from './enhanced-analysis/ModalFooter';
import TabContent from './enhanced-analysis/TabContent';
import { Brain, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

export default function EnhancedAdvancedAnalysisModal({ 
  isOpen, 
  onClose, 
  analysis, 
  insight 
}: EnhancedAdvancedAnalysisModalProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSection, setActiveSection] = useState('overview');
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [analysisStats, setAnalysisStats] = useState({
    totalSections: 0,
    sectionsWithContent: 0,
    dataQuality: 'unknown' as 'high' | 'medium' | 'low' | 'unknown'
  });

  const { user } = useAuth();
  const sections = parseUltraDetailedAnalysis(analysis);

  // Calculate analysis statistics
  useEffect(() => {
    const total = sections.length;
    const withContent = sections.filter(s => 
      s.content && 
      s.content.length > 50 && 
      !s.content.includes('não disponível')
    ).length;
    
    let quality: 'high' | 'medium' | 'low' | 'unknown' = 'unknown';
    if (total > 0) {
      const ratio = withContent / total;
      if (ratio >= 0.8) quality = 'high';
      else if (ratio >= 0.5) quality = 'medium';
      else quality = 'low';
    }

    setAnalysisStats({
      totalSections: total,
      sectionsWithContent: withContent,
      dataQuality: quality
    });
    
    // Auto-expand sections with content if there are few of them
    if (withContent <= 3) {
      const sectionsWithContentIndexes = sections
        .map((s, i) => ({ section: s, index: i }))
        .filter(({ section }) => 
          section.content && 
          section.content.length > 50 && 
          !section.content.includes('não disponível')
        )
        .map(({ index }) => index);
      
      setExpandedSections(new Set(sectionsWithContentIndexes));
    }
  }, [sections]);

  const toggleSection = (index: number) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedSections(newExpanded);
  };

  const expandAll = () => {
    const allIndexes = new Set(sections.map((_, index) => index));
    setExpandedSections(allIndexes);
  };

  const collapseAll = () => {
    setExpandedSections(new Set());
  };

  // Enhanced PDF download function
  const downloadUltraDetailedPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      toast.loading('Gerando PDF com design profissional...', { id: 'pdf-generation' });

      await generateUltraDetailedPDF({
        sections,
        insightId: insight.id,
        createdAt: insight.created_at,
        userProfile: {
          name: user?.user_metadata?.name || user?.email?.split('@')[0],
          email: user?.email
        }
      });

      toast.success('PDF gerado com sucesso!', { id: 'pdf-generation' });
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error('Erro ao gerar PDF. Tente novamente.', { id: 'pdf-generation' });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Filter sections based on search term
  const filteredSections = sections.filter(section => 
    section.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    section.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group sections by category
  const groupedSections: GroupedSections = {
    overview: filteredSections.filter(s => s.category === 'overview'),
    traits: filteredSections.filter(s => s.category === 'traits'),
    psychology: filteredSections.filter(s => s.category === 'psychology'),
    clinical: filteredSections.filter(s => s.category === 'clinical'),
    recommendations: filteredSections.filter(s => s.category === 'recommendations')
  };

  const getQualityBadge = () => {
    const { dataQuality, sectionsWithContent, totalSections } = analysisStats;
    
    switch (dataQuality) {
      case 'high':
        return <Badge variant="default" className="bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          Análise Completa ({sectionsWithContent}/{totalSections})
        </Badge>;
      case 'medium':
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
          <AlertCircle className="w-3 h-3 mr-1" />
          Análise Parcial ({sectionsWithContent}/{totalSections})
        </Badge>;
      case 'low':
        return <Badge variant="outline" className="bg-red-100 text-red-800">
          <AlertCircle className="w-3 h-3 mr-1" />
          Dados Limitados ({sectionsWithContent}/{totalSections})
        </Badge>;
      default:
        return <Badge variant="outline">Processando...</Badge>;
    }
  };

  if (!analysis) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[95vh] overflow-hidden">
        <ModalHeader
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onExpandAll={expandAll}
          onCollapseAll={collapseAll}
          onDownload={downloadUltraDetailedPDF}
        />

        {/* Data Quality Alert */}
        <div className="flex items-center justify-between mb-4">
          {getQualityBadge()}
          
          {analysisStats.dataQuality === 'low' && (
            <Alert className="flex-1 ml-4">
              <Brain className="h-4 w-4" />
              <AlertDescription className="text-sm">
                Esta análise contém dados limitados. Algumas seções podem estar aguardando processamento adicional.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <Tabs value={activeSection} onValueChange={setActiveSection} className="flex-1">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview" className="text-xs">
              Visão Geral ({groupedSections.overview.length})
            </TabsTrigger>
            <TabsTrigger value="traits" className="text-xs">
              Traços ({groupedSections.traits.length})
            </TabsTrigger>
            <TabsTrigger value="psychology" className="text-xs">
              Psicologia ({groupedSections.psychology.length})
            </TabsTrigger>
            <TabsTrigger value="clinical" className="text-xs">
              Clínico ({groupedSections.clinical.length})
            </TabsTrigger>
            <TabsTrigger value="recommendations" className="text-xs">
              Recomendações ({groupedSections.recommendations.length})
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="max-h-[65vh] w-full mt-4">
            <TabContent
              value="overview"
              sections={groupedSections.overview}
              expandedSections={expandedSections}
              onToggleSection={toggleSection}
              baseIndex={0}
            />

            <TabContent
              value="traits"
              sections={groupedSections.traits}
              expandedSections={expandedSections}
              onToggleSection={toggleSection}
              baseIndex={groupedSections.overview.length}
            />

            <TabContent
              value="psychology"
              sections={groupedSections.psychology}
              expandedSections={expandedSections}
              onToggleSection={toggleSection}
              baseIndex={groupedSections.overview.length + groupedSections.traits.length}
            />

            <TabContent
              value="clinical"
              sections={groupedSections.clinical}
              expandedSections={expandedSections}
              onToggleSection={toggleSection}
              baseIndex={groupedSections.overview.length + groupedSections.traits.length + groupedSections.psychology.length}
            />

            <TabContent
              value="recommendations"
              sections={groupedSections.recommendations}
              expandedSections={expandedSections}
              onToggleSection={toggleSection}
              baseIndex={groupedSections.overview.length + groupedSections.traits.length + groupedSections.psychology.length + groupedSections.clinical.length}
            />
          </ScrollArea>
        </Tabs>

        <ModalFooter
          insightId={insight.id}
          createdAt={insight.created_at}
          onClose={onClose}
        />
      </DialogContent>
    </Dialog>
  );
}
